import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Heart, Sparkles, BookOpen, Users, ArrowRight, Feather, Star } from 'lucide-react';

export default function LandingPage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1a1a2e]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-xl border-b border-[#1a1a2e]/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#7c3aed] to-[#db2777] flex items-center justify-center shadow-lg shadow-purple-500/20">
              <Heart className="w-4.5 h-4.5 text-white" strokeWidth={2} fill="currentColor" />
            </div>
            <span className="text-xl font-bold tracking-tight">iExtol</span>
          </Link>
          <nav className="flex items-center gap-3">
            <Link to="/explore">
              <Button variant="ghost" className="text-[#1a1a2e]/70 hover:text-[#1a1a2e] hover:bg-[#1a1a2e]/5 rounded-full px-5" data-testid="explore-button">
                Explore
              </Button>
            </Link>
            {user ? (
              <Link to="/dashboard">
                <Button className="bg-[#1a1a2e] hover:bg-[#2d2d4a] text-white rounded-full px-6 font-medium" data-testid="dashboard-button">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-[#1a1a2e]/70 hover:text-[#1a1a2e] hover:bg-[#1a1a2e]/5 rounded-full px-5" data-testid="login-button">
                    Sign in
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-gradient-to-r from-[#7c3aed] to-[#db2777] hover:from-[#6d28d9] hover:to-[#be185d] text-white rounded-full px-6 font-medium shadow-lg shadow-purple-500/20" data-testid="register-button">
                    Get started
                  </Button>
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-32 w-[500px] h-[500px] bg-purple-200/40 rounded-full blur-[100px]" />
          <div className="absolute bottom-1/4 -right-32 w-[400px] h-[400px] bg-pink-200/40 rounded-full blur-[80px]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-100/50 rounded-full blur-[120px]" />
        </div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/80 border border-[#1a1a2e]/10 rounded-full px-4 py-2 mb-8 shadow-sm">
              <Feather className="w-4 h-4 text-[#7c3aed]" strokeWidth={1.5} />
              <span className="text-sm text-[#1a1a2e]/70 font-medium">For the quiet heroes in our lives</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] mb-8">
              Honour the people who
              <span className="block mt-2 bg-gradient-to-r from-[#7c3aed] via-[#db2777] to-[#f59e0b] bg-clip-text text-transparent">
                shaped who you are
              </span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-[#1a1a2e]/60 leading-relaxed mb-12 max-w-2xl">
              Behind every person is someone who believed in them, sacrificed for them, inspired them. 
              Tell their story. Let the world know their impact.
            </p>
            
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <Link to="/register">
                <Button size="lg" className="bg-gradient-to-r from-[#7c3aed] to-[#db2777] hover:from-[#6d28d9] hover:to-[#be185d] text-white rounded-full px-10 py-7 text-lg font-semibold shadow-xl shadow-purple-500/25 transition-all duration-300 hover:-translate-y-1" data-testid="hero-cta-button">
                  Create a tribute
                  <ArrowRight className="ml-2 w-5 h-5" strokeWidth={2} />
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" size="lg" className="border-2 border-[#1a1a2e]/20 text-[#1a1a2e] hover:bg-[#1a1a2e]/5 rounded-full px-10 py-7 text-lg" data-testid="hero-secondary-button">
                  Sign in
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Decorative cards */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1/3 h-[500px] hidden lg:block">
          <div className="relative w-full h-full">
            <div className="absolute right-16 top-12 w-56 h-72 rounded-3xl bg-white shadow-2xl shadow-[#1a1a2e]/10 border border-[#1a1a2e]/5 transform rotate-6" />
            <div className="absolute right-28 top-24 w-56 h-72 rounded-3xl bg-gradient-to-br from-[#7c3aed]/10 to-[#db2777]/10 shadow-2xl border border-[#1a1a2e]/5 transform -rotate-3" />
          </div>
        </div>
      </section>

      {/* Why Section */}
      <section className="py-32 relative bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight mb-6">
              Some people change
              <span className="block text-[#1a1a2e]/30">everything</span>
            </h2>
            <p className="text-xl text-[#1a1a2e]/50 max-w-2xl mx-auto">
              They don't seek recognition. They give without expecting anything in return. 
              They are the reason you are who you are today.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: 'The quiet sacrifices',
                description: 'The parent who worked extra shifts. The friend who showed up when no one else did. The mentor who saw potential when you couldn\'t.',
                color: 'purple',
                gradient: 'from-[#7c3aed]/10 to-[#7c3aed]/5'
              },
              {
                title: 'The lasting impact',
                description: 'A word of encouragement that changed your path. An act of kindness you\'ll never forget. The belief that pushed you forward.',
                color: 'pink',
                gradient: 'from-[#db2777]/10 to-[#db2777]/5'
              },
              {
                title: 'The untold stories',
                description: 'Their story deserves to be told. Their impact deserves to be remembered. Their legacy deserves to inspire others.',
                color: 'amber',
                gradient: 'from-[#f59e0b]/10 to-[#f59e0b]/5'
              }
            ].map((item) => (
              <div 
                key={item.title}
                className={`group relative p-8 rounded-3xl bg-gradient-to-b ${item.gradient} border border-[#1a1a2e]/5 hover:shadow-xl hover:-translate-y-1 transition-all duration-300`}
              >
                <h3 className="text-xl font-bold mb-4">{item.title}</h3>
                <p className="text-[#1a1a2e]/60 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Profile Types */}
      <section className="py-32 relative bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#7c3aed] text-sm font-semibold tracking-wider uppercase mb-4">Two ways to honour</p>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight">
              Their story, your words
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Living Tribute */}
            <div className="group" data-testid="living-tribute-card">
              <div className="p-10 rounded-3xl bg-white border border-[#1a1a2e]/5 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/20">
                  <Sparkles className="w-8 h-8 text-white" strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl font-bold mb-2">Living Tribute</h3>
                <p className="text-emerald-600 text-sm font-semibold mb-4">Tell them while they can hear it</p>
                <p className="text-[#1a1a2e]/60 leading-relaxed">
                  Don't wait. Let them know how they've impacted your life. 
                  Share what they mean to you while they're here to receive it.
                </p>
              </div>
            </div>

            {/* Legacy Tribute */}
            <div className="group" data-testid="legacy-tribute-card">
              <div className="p-10 rounded-3xl bg-white border border-[#1a1a2e]/5 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center mb-6 shadow-lg shadow-violet-500/20">
                  <Star className="w-8 h-8 text-white" strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl font-bold mb-2">Legacy Tribute</h3>
                <p className="text-violet-600 text-sm font-semibold mb-4">Keep their light alive</p>
                <p className="text-[#1a1a2e]/60 leading-relaxed">
                  Preserve the memory of those who have passed. 
                  Their story, their impact, their legacy—forever remembered.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-32 relative bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#db2777] text-sm font-semibold tracking-wider uppercase mb-4">How it works</p>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight mb-6">
              Capture what matters
            </h2>
            <p className="text-xl text-[#1a1a2e]/50 max-w-2xl mx-auto">
              Guided storytelling that helps you articulate the profound impact someone has had on your life.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: BookOpen,
                title: 'Tell Their Story',
                description: 'Guided prompts help you capture their journey, the sacrifices they made, and the person they are.',
                color: '#7c3aed'
              },
              {
                icon: Heart,
                title: 'Share The Impact',
                description: 'Describe how they changed your life. The moments that mattered. The lessons that stayed.',
                color: '#db2777'
              },
              {
                icon: Users,
                title: 'Gather Voices',
                description: 'Invite others to contribute their stories. Build a rich tapestry of one person\'s impact on many.',
                color: '#f59e0b'
              }
            ].map((feature) => (
              <div key={feature.title} className="text-center p-8 rounded-3xl bg-[#FDFBF7] border border-[#1a1a2e]/5">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
                  style={{ backgroundColor: `${feature.color}15` }}
                >
                  <feature.icon className="w-8 h-8" style={{ color: feature.color }} strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-[#1a1a2e]/60 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-32 relative bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="relative">
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-8xl text-[#1a1a2e]/5 font-serif">"</div>
            <blockquote className="text-3xl sm:text-4xl font-light leading-relaxed text-[#1a1a2e]/80 italic relative z-10">
              Behind every confident person is someone who believed in them first. 
              Behind every success is someone who sacrificed to make it possible.
            </blockquote>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 relative bg-gradient-to-br from-[#1a1a2e] to-[#2d2d4a]">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] bg-pink-500/10 rounded-full blur-[80px]" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tight mb-6 text-white">
            Who shaped your story?
          </h2>
          <p className="text-xl text-white/60 mb-4 max-w-2xl mx-auto">
            Many people go through life never knowing how they have made a positive impact on someone's life.
          </p>
          <p className="text-lg text-white/40 mb-10 max-w-2xl mx-auto">
            Someone believed in you. Someone sacrificed for you. 
            Let them know. Create your first tribute completely free.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-[#1a1a2e] hover:bg-white/90 rounded-full px-12 py-7 text-lg font-semibold shadow-2xl transition-all duration-300 hover:-translate-y-1" data-testid="cta-button">
              Create a free tribute
              <ArrowRight className="ml-2 w-5 h-5" strokeWidth={2} />
            </Button>
          </Link>
          <p className="text-white/30 text-sm mt-4">No credit card needed. Your first tribute is always free.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#1a1a2e]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#7c3aed] to-[#db2777] flex items-center justify-center">
                <Heart className="w-4 h-4 text-white" strokeWidth={2} fill="currentColor" />
              </div>
              <span className="font-semibold text-white">iExtol</span>
            </div>
            <nav className="flex items-center gap-6">
              <Link to="/explore" className="text-white/40 hover:text-white/70 text-sm transition-colors">Explore Tributes</Link>
              <Link to="/register" className="text-white/40 hover:text-white/70 text-sm transition-colors">Create a Tribute</Link>
            </nav>
          </div>
          <div className="border-t border-white/10 pt-6 text-center">
            <p className="text-white/30 text-sm italic mb-3">
              "Many people go through life never knowing how they have made a positive impact on someone's life."
            </p>
            <p className="text-white/40 text-sm">
              &copy; {new Date().getFullYear()} Resilience Community Collective. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
